import Decimal from 'decimal.js'
import { z } from 'zod'

/**
 * Monedas en las que la plataforma OPERA: precios, promesas, órdenes.
 *
 * Es deliberadamente corta. Un inmueble cotizado en una moneda que la empresa no
 * factura no es una funcionalidad, es un problema contable esperando.
 */
export const MONEY_CURRENCIES = ['GTQ', 'USD'] as const
export const CurrencySchema = z.enum(MONEY_CURRENCIES)
export type Currency = z.infer<typeof CurrencySchema>

/**
 * Monedas que un cliente puede DECLARAR, que no es lo mismo.
 *
 * Una cuenta del estado patrimonial no es dinero que la plataforma mueva: es una
 * declaración patrimonial de KYC sobre una cuenta que existe en algún banco del
 * mundo. Producción tiene dos clientes vivos con cuentas en Banco Sabadell
 * —IBAN español, saldos de seis cifras— declaradas en euros.
 *
 * Por eso son dos conjuntos y no uno ensanchado: que un cliente pueda declarar
 * euros NO puede implicar que un inmueble se pueda cotizar en euros. El
 * interceptor del wire hace exactamente esa distinción — un `currency` suelto
 * admite las declarables; el `currency` de un `Money` (el que viene con
 * `amountMinor`) sigue admitiendo sólo las operables.
 *
 * ES LA LISTA COMPLETA DE ISO 4217, y no una selección. Fue tres monedas y eso
 * bastó hasta que un cliente declaró una cuenta HSBC en dírhams: el formulario
 * la aceptó —el campo era texto libre— y el expediente entero dejó de cargar.
 * Recortar el catálogo no evita ese dato, sólo le saca el nombre correcto. Una
 * declaración de KYC dice qué tiene el cliente, y el cliente no elige su moneda
 * de una lista nuestra.
 *
 * Generada desde ICU (`Intl.supportedValuesOf('currency')`) y fijada acá a
 * propósito: un contrato no puede depender de la versión de ICU del runtime, o
 * cambiaría solo entre una máquina y otra. Los NOMBRES sí se resuelven con
 * `Intl.DisplayNames` en el front, porque eso es presentación y no contrato.
 */
export const DECLARED_CURRENCIES = [
  'AED',
  'AFN',
  'ALL',
  'AMD',
  'ANG',
  'AOA',
  'ARS',
  'AUD',
  'AWG',
  'AZN',
  'BAM',
  'BBD',
  'BDT',
  'BGN',
  'BHD',
  'BIF',
  'BMD',
  'BND',
  'BOB',
  'BRL',
  'BSD',
  'BTN',
  'BWP',
  'BYN',
  'BZD',
  'CAD',
  'CDF',
  'CHF',
  'CLP',
  'CNY',
  'COP',
  'CRC',
  'CUC',
  'CUP',
  'CVE',
  'CZK',
  'DJF',
  'DKK',
  'DOP',
  'DZD',
  'EGP',
  'ERN',
  'ETB',
  'EUR',
  'FJD',
  'FKP',
  'GBP',
  'GEL',
  'GHS',
  'GIP',
  'GMD',
  'GNF',
  'GTQ',
  'GYD',
  'HKD',
  'HNL',
  'HRK',
  'HTG',
  'HUF',
  'IDR',
  'ILS',
  'INR',
  'IQD',
  'IRR',
  'ISK',
  'JMD',
  'JOD',
  'JPY',
  'KES',
  'KGS',
  'KHR',
  'KMF',
  'KPW',
  'KRW',
  'KWD',
  'KYD',
  'KZT',
  'LAK',
  'LBP',
  'LKR',
  'LRD',
  'LSL',
  'LYD',
  'MAD',
  'MDL',
  'MGA',
  'MKD',
  'MMK',
  'MNT',
  'MOP',
  'MRU',
  'MUR',
  'MVR',
  'MWK',
  'MXN',
  'MYR',
  'MZN',
  'NAD',
  'NGN',
  'NIO',
  'NOK',
  'NPR',
  'NZD',
  'OMR',
  'PAB',
  'PEN',
  'PGK',
  'PHP',
  'PKR',
  'PLN',
  'PYG',
  'QAR',
  'RON',
  'RSD',
  'RUB',
  'RWF',
  'SAR',
  'SBD',
  'SCR',
  'SDG',
  'SEK',
  'SGD',
  'SHP',
  'SLE',
  'SLL',
  'SOS',
  'SRD',
  'SSP',
  'STN',
  'SVC',
  'SYP',
  'SZL',
  'THB',
  'TJS',
  'TMT',
  'TND',
  'TOP',
  'TRY',
  'TTD',
  'TWD',
  'TZS',
  'UAH',
  'UGX',
  'USD',
  'UYU',
  'UZS',
  'VES',
  'VND',
  'VUV',
  'WST',
  'XAF',
  'XCD',
  'XCG',
  'XDR',
  'XOF',
  'XPF',
  'XSU',
  'YER',
  'ZAR',
  'ZMW',
  'ZWG',
  'ZWL'
] as const
export const DeclaredCurrencySchema = z.enum(DECLARED_CURRENCIES)
export type DeclaredCurrency = z.infer<typeof DeclaredCurrencySchema>

export const MoneySchema = z.object({
  amountMinor: z.number().safe().int(),
  currency: CurrencySchema
})
export type Money = z.infer<typeof MoneySchema>

export const NonNegativeMoneySchema = MoneySchema.refine((value) => value.amountMinor >= 0, {
  message: 'Amount must be non-negative',
  path: ['amountMinor']
})

function assertSameCurrency(values: readonly Money[]): Currency {
  const currency = values[0]?.currency
  if (!currency) throw new RangeError('At least one monetary value is required')

  if (values.some((value) => value.currency !== currency)) {
    throw new RangeError('Cannot operate on values with different currencies')
  }
  return currency
}

function toSafeMinor(value: Decimal): number {
  const amountMinor = value.toNumber()
  if (!Number.isSafeInteger(amountMinor)) {
    throw new RangeError('Monetary result exceeds JavaScript safe integer range')
  }
  return amountMinor
}

export function money(amountMinor: number, currency: Currency): Money {
  return MoneySchema.parse({ amountMinor, currency })
}

/** Convierte una cantidad mayor una sola vez con ROUND_HALF_UP. */
export function moneyFromMajor(
  amountMajor: Decimal.Value,
  currency: Currency,
  fractionDigits = 2
): Money {
  if (!Number.isSafeInteger(fractionDigits) || fractionDigits < 0 || fractionDigits > 6) {
    throw new RangeError('fractionDigits must be an integer between 0 and 6')
  }

  const factor = new Decimal(10).pow(fractionDigits)
  const amountMinor = new Decimal(amountMajor)
    .times(factor)
    .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)

  return money(toSafeMinor(amountMinor), currency)
}

export function addMoney(...values: readonly Money[]): Money {
  const currency = assertSameCurrency(values)
  const total = values.reduce(
    (accumulator, value) => accumulator.plus(value.amountMinor),
    new Decimal(0)
  )
  return money(toSafeMinor(total), currency)
}

/**
 * Prorratea dinero no negativo por peso y conserva exactamente el total.
 * Los residuos se asignan por mayor fracción y luego por orden de entrada.
 */
export function allocateMoney(value: Money, weights: readonly number[]): Money[] {
  NonNegativeMoneySchema.parse(value)
  if (weights.length === 0) throw new RangeError('At least one allocation weight is required')
  if (weights.some((weight) => !Number.isFinite(weight) || weight < 0)) {
    throw new RangeError('Allocation weights must be finite and non-negative')
  }

  const totalWeight = weights.reduce((total, weight) => total.plus(weight), new Decimal(0))
  if (totalWeight.isZero()) throw new RangeError('Allocation weights must add up to more than zero')

  const exact = weights.map((weight) =>
    new Decimal(value.amountMinor).times(weight).dividedBy(totalWeight)
  )
  const allocated = exact.map((share) => share.floor().toNumber())
  let remaining = value.amountMinor - allocated.reduce((total, share) => total + share, 0)

  const byLargestRemainder = exact
    .map((share, index) => ({ index, remainder: share.minus(share.floor()) }))
    .sort((left, right) => {
      const comparison = right.remainder.comparedTo(left.remainder)
      return comparison === 0 ? left.index - right.index : comparison
    })

  for (const { index } of byLargestRemainder) {
    if (remaining === 0) break
    allocated[index] += 1
    remaining -= 1
  }

  return allocated.map((amountMinor) => money(amountMinor, value.currency))
}
